import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import { createParser, type EventSourceMessage } from 'eventsource-parser';
import type { ResearchArtifact } from '@/reactQuery/researchAssistant';
import useGlobalProgressStore from '@/store/GlobalProgressStore';
import useTranslation from '@/hooks/useTranslation';
import type { SafeErrorPayload } from './safeErrorTypes';
import type { RASubstep, RANextAction } from './raEventTypes';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  artifacts?: ResearchArtifact[];
  tool_steps?: ToolStep[];
  created_at: string;
  /** Set when the response was cut short (max_tokens or connection_closed). */
  truncation_reason?: 'max_tokens' | 'connection_closed' | null;
}

export interface ToolStep {
  tool_name: string;
  tool_call_id: string;
  status: 'running' | 'done' | 'error' | 'stale';
  /**
   * Unix epoch (ms) when the backend first reported the tool as
   * `running`. Used by ToolProgressStepper to render an elapsed-time
   * counter so users know something is still happening during long
   * tool calls like `generate_artifact`, which can take 1-2 minutes
   * for large analysis requests.
   */
  startedAt?: number;
  /** Unix epoch (ms) when the tool transitioned to done/error. */
  completedAt?: number;
  /**
   * Substep progress from D.6 substep events.
   * Populated when the backend emits a `tool_status` event with a `substep`
   * field (only for long-running tools that exceed the 5s gate).
   */
  substep?: RASubstep;
  /**
   * Inline action hint from D.7.
   * Populated on `status: 'stale'` events for `get_comparison` — tells the
   * frontend to render a "Refresh this comparison" button.
   */
  next_action?: RANextAction;
}

interface UseResearchChatReturn {
  messages: ChatMessage[];
  artifacts: ResearchArtifact[];
  isStreaming: boolean;
  /** Structured error payload. Replaces the previous `string | null` so the full
   *  SafeErrorPayload (code + correlationId + retriable) survives to render time. */
  error: SafeErrorPayload | null;
  /**
   * Send a message. Optionally accepts an explicit threadId override for the
   * case where the caller has just created a thread and React state hasn't
   * propagated yet.
   */
  sendMessage: (message: string, threadIdOverride?: string) => Promise<void>;
  stopStreaming: () => void;
  /** Retry the last failed message — removes failed messages, clears error, re-sends. */
  retry: () => void;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  setArtifacts: React.Dispatch<React.SetStateAction<ResearchArtifact[]>>;
  activeToolSteps: ToolStep[];
}

export default function useResearchChat(
  projectId: string | undefined,
  threadId: string | null,
  filterId: string | null,
  onDone?: () => void,
): UseResearchChatReturn {
  const { lang } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [artifacts, setArtifacts] = useState<ResearchArtifact[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<SafeErrorPayload | null>(null);
  const lastSentMessageRef = useRef<string | null>(null);
  const [activeToolSteps, setActiveToolSteps] = useState<ToolStep[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  // Read isStreaming via ref so sendMessage's identity is stable across
  // streaming toggles — preventing cascading recreations of dependent callbacks.
  const isStreamingRef = useRef(false);

  // Token buffering: accumulate delta tokens and flush in batches via
  // requestAnimationFrame to reduce renders from ~1000 (per token) to ~50-60
  // (per animation frame). Falls back to setTimeout(16) in hidden tabs where
  // browsers throttle rAF.
  const tokenBufferRef = useRef('');
  const tokenFlushScheduledRef = useRef(false);
  const tokenAssistantIdRef = useRef<string | null>(null);
  const queryClient = useQueryClient();

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
    isStreamingRef.current = false;
    // Reset the token-flush buffer and its scheduled-flush flag so a
    // rAF callback that was queued just before this abort doesn't fire
    // on the next frame and try to append a stale buffer to whichever
    // assistant message is current at that moment (e.g. after the user
    // switched threads). tokenAssistantIdRef stays as-is — the rAF
    // guards the write on `messages.some(m => m._id === aid)`, and
    // nulling it here would only prevent the rAF from ever running.
    tokenBufferRef.current = '';
    tokenFlushScheduledRef.current = false;
  }, []);

  const sendMessage = useCallback(
    async (message: string, threadIdOverride?: string) => {
      const effectiveThreadId = threadIdOverride ?? threadId;
      if (!projectId || !effectiveThreadId || isStreamingRef.current) return;

      lastSentMessageRef.current = message;
      setError(null);
      setIsStreaming(true);
      isStreamingRef.current = true;
      setActiveToolSteps([]);

      // Publish to global progress bar so the user knows RA is working
      // even when viewing another tab. The bar auto-hides on this
      // project's Results page (inline stepper covers that) and shows
      // everywhere else.
      useGlobalProgressStore.getState().addEntry({
        id: `ra-${effectiveThreadId}`,
        type: 'research_assistant',
        label: lang.get('msg.raWorking'),
        startedAt: Date.now(),
        projectId,
        returnUrl: `${window.location.pathname}${window.location.search}`,
      });

      // Add user message immediately. Use uuid to guarantee uniqueness even
      // when messages are sent in the same millisecond (Date.now() collisions).
      const userMsg: ChatMessage = {
        id: `user-${uuidv4()}`,
        role: 'user',
        content: message,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);

      // Add placeholder assistant message for streaming
      const assistantId = `assistant-${uuidv4()}`;
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        artifacts: [],
        tool_steps: [],
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMsg]);

      const abortController = new AbortController();
      abortRef.current = abortController;
      let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;

      try {
        const token = Cookies.get('token');
        const baseUrl = import.meta.env.VITE_API_BASE_URL_ANALYZE;
        const url = `${baseUrl}/analyzer/research-assistant/${projectId}/threads/${effectiveThreadId}/chat`;

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message,
            filter_id: filterId,
          }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          // Try to parse a structured error from the body. NestJS typically
          // returns { statusCode, message, error } for thrown HttpExceptions.
          let serverMessage = `${response.status}: ${response.statusText}`;
          try {
            const errBody = await response.json();
            if (errBody?.message) {
              serverMessage = Array.isArray(errBody.message)
                ? errBody.message.join(', ')
                : errBody.message;
            }
          } catch {
            // Body wasn't JSON — keep the status line
          }
          throw new Error(serverMessage);
        }

        reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        // handleSSEEvent is defined after sendMessage because it does not depend on
        // sendMessage; moving it above would require splitting the hook or duplicating
        // state references. The function is stable via useCallback, so the call is safe.
        const parser = createParser({
          onEvent: (event: EventSourceMessage) => {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            handleSSEEvent(event, assistantId);
          },
        });

        // SSE requires sequential reads — each chunk must be processed in order
        // so the parser can reconstruct event boundaries correctly. Promise.all
        // is not applicable here because the reader is a pull-based stream.
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          parser.feed(chunk);
        }
      } catch (err: unknown) {
        const errObj = err instanceof Error ? err : null;
        if (errObj?.name === 'AbortError') {
          // User cancelled — keep partial response so they can read what
          // was generated before the abort
        } else {
          // Network / fetch-level error — no SafeErrorPayload from the backend.
          // Construct a GENERIC payload so the banner still renders consistently.
          setError({
            code: 'GENERIC',
            message: errObj?.message || 'Failed to send message',
            correlationId: '',
            retriable: true,
          });
          // Remove the placeholder assistant message if it has no content;
          // partial responses are kept so the user sees what was generated
          // before the error.
          setMessages((prev) => prev.filter((m) => m.id !== assistantId || m.content.length > 0));
        }
      } finally {
        reader?.cancel().catch(() => {});
        setIsStreaming(false);
        isStreamingRef.current = false;
        setActiveToolSteps([]);
        abortRef.current = null;
        // Remove from global progress bar
        useGlobalProgressStore.getState().removeEntry(`ra-${effectiveThreadId}`);
        // Invalidate thread queries to refresh state. Use the full key to
        // avoid invalidating thread lists for other projects/filters.
        queryClient.invalidateQueries({
          queryKey: ['research-threads', projectId, filterId ?? 'all'],
        });
        queryClient.invalidateQueries({
          queryKey: ['research-thread', projectId, effectiveThreadId],
        });
      }
    },
    // Note: isStreaming is intentionally NOT in deps — read via ref above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, threadId, filterId, queryClient],
  );

  const handleSSEEvent = useCallback(
    (event: EventSourceMessage, assistantId: string) => {
      if (!event.data) return;

      try {
        const data = JSON.parse(event.data);

        switch (event.event) {
          case 'delta':
            // Buffer tokens and flush per animation frame to reduce renders.
            tokenBufferRef.current += data.text || '';
            tokenAssistantIdRef.current = assistantId;
            if (!tokenFlushScheduledRef.current) {
              tokenFlushScheduledRef.current = true;
              const flush = () => {
                const buffered = tokenBufferRef.current;
                const aid = tokenAssistantIdRef.current;
                tokenBufferRef.current = '';
                tokenFlushScheduledRef.current = false;
                if (buffered && aid) {
                  setMessages((prev) =>
                    prev.map((m) => (m.id === aid ? { ...m, content: m.content + buffered } : m)),
                  );
                }
              };
              if (document.hidden) {
                setTimeout(flush, 16);
              } else {
                requestAnimationFrame(flush);
              }
            }
            break;

          case 'delta_replace':
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, content: data.text || '' } : m)),
            );
            break;

          case 'artifact':
            setArtifacts((prev) => [...prev, data as ResearchArtifact]);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? {
                      ...m,
                      artifacts: [...(m.artifacts || []), data as ResearchArtifact],
                    }
                  : m,
              ),
            );
            break;

          case 'tool_status': {
            // Record the first-seen timestamp for a tool call so the
            // stepper can render an elapsed-time counter. If the tool
            // is already in state (status transition from running →
            // done/error/stale), preserve the original startedAt so the
            // final duration in the collapsed view reflects the full run.
            //
            // D.6: also parse `data.substep` — the backend emits substep
            // events on the SAME event type as status transitions. The
            // substep handler MUST NOT block token rendering — any parse
            // error here is caught by the outer try/catch which allows
            // the token stream to continue uninterrupted (D.6 LLM-R5).
            //
            // D.7: also parse `data.next_action` for stale-comparison affordance.
            const now = Date.now();
            const incomingSubstep: RASubstep | undefined =
              data.substep && typeof data.substep.name === 'string'
                ? (data.substep as RASubstep)
                : undefined;
            const incomingNextAction: RANextAction | undefined =
              data.next_action && typeof data.next_action.kind === 'string'
                ? (data.next_action as RANextAction)
                : undefined;

            setActiveToolSteps((prev) => {
              const existing = prev.find((s) => s.tool_call_id === data.tool_call_id);
              if (existing) {
                return prev.map((s) =>
                  s.tool_call_id === data.tool_call_id
                    ? {
                        ...s,
                        status: data.status,
                        ...(data.status !== 'running' && !s.completedAt
                          ? { completedAt: now }
                          : {}),
                        // Update substep in-place; keep prior substep if this
                        // event has no substep (e.g. the final 'done' event).
                        ...(incomingSubstep !== undefined ? { substep: incomingSubstep } : {}),
                        // Attach next_action when present (D.7)
                        ...(incomingNextAction !== undefined
                          ? { next_action: incomingNextAction }
                          : {}),
                      }
                    : s,
                );
              }
              // When a new 'running' step arrives for a tool_name that
              // already has an errored step, replace the error — the model
              // is retrying (new tool_call_id) and the user shouldn't see
              // the failed attempt's red "!" alongside the retry.
              if (data.status === 'running') {
                const erroredSameToolIdx = prev.findIndex(
                  (s) => s.tool_name === data.tool_name && s.status === 'error',
                );
                if (erroredSameToolIdx !== -1) {
                  const filtered = prev.filter((_, i) => i !== erroredSameToolIdx);
                  return [
                    ...filtered,
                    {
                      ...(data as ToolStep),
                      startedAt: now,
                      substep: incomingSubstep,
                      next_action: incomingNextAction,
                    },
                  ];
                }
              }
              return [
                ...prev,
                {
                  ...(data as ToolStep),
                  startedAt: now,
                  substep: incomingSubstep,
                  next_action: incomingNextAction,
                },
              ];
            });
            // Also attach to message, preserving any prior startedAt.
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== assistantId) return m;
                const existing = (m.tool_steps || []).find(
                  (s) => s.tool_call_id === data.tool_call_id,
                );
                const nextStep: ToolStep = existing
                  ? {
                      ...existing,
                      status: data.status,
                      ...(data.status !== 'running' && !existing.completedAt
                        ? { completedAt: now }
                        : {}),
                      ...(incomingSubstep !== undefined ? { substep: incomingSubstep } : {}),
                      ...(incomingNextAction !== undefined
                        ? { next_action: incomingNextAction }
                        : {}),
                    }
                  : {
                      ...(data as ToolStep),
                      startedAt: now,
                      substep: incomingSubstep,
                      next_action: incomingNextAction,
                    };
                return {
                  ...m,
                  tool_steps: [
                    ...(m.tool_steps || []).filter((s) => s.tool_call_id !== data.tool_call_id),
                    nextStep,
                  ],
                };
              }),
            );
            break;
          }

          case 'error': {
            // D.2: Upgrade from string error to structured SafeErrorPayload.
            // The backend emits 'ANALYSIS_OUTDATED' (not 'RA_ANALYSIS_OUTDATED'
            // which was the previous dead-code branch). We invalidate the
            // schema-status cache so the OutdatedAnalysisDialog re-shows,
            // giving the user an explicit path to resolve the stale state.
            // The banner's "Resolve outdated analysis" CTA (D.2-R3) also
            // calls this via onReRunAnalysis — two triggers, one outcome.
            if (data.code === 'ANALYSIS_OUTDATED') {
              queryClient.invalidateQueries({
                queryKey: ['ra-analysis-schema-status', projectId],
              });
            }

            // Build the SafeErrorPayload from the SSE data.  The backend always
            // emits code + message + correlationId; retriable is optional.
            const errorPayload: SafeErrorPayload = {
              code: data.code ?? 'GENERIC',
              message: data.message ?? 'An error occurred',
              correlationId: data.correlationId ?? '',
              retriable: data.retriable,
              rate_limited: !!data.rate_limited,
              retryAfterSec: data.retryAfterSec,
            };
            setError(errorPayload);
            // Defensively abort the stream — the backend should also end
            // the connection after writing 'error', but if it doesn't (or if
            // a buggy intermediary keeps the connection alive), we don't
            // want stale deltas to keep arriving.
            abortRef.current?.abort();
            break;
          }

          case 'truncated':
            // The backend detected that the response was cut short (max_tokens
            // or connection_closed). Mark the message so the UI can render a
            // "Continue" button instead of leaving a broken sentence.
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, truncation_reason: data.reason || 'max_tokens' } : m,
              ),
            );
            break;

          case 'done': {
            // Flush any remaining buffered tokens before marking stream as done
            if (tokenBufferRef.current) {
              const remaining = tokenBufferRef.current;
              tokenBufferRef.current = '';
              tokenFlushScheduledRef.current = false;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId ? { ...m, content: m.content + remaining } : m,
                ),
              );
            }
            // Notify the user if they navigated away from the RA tab
            // while the request was running. The RA tab uses forceMount
            // so the SSE connection persists, but without a notification
            // the user has no way to know results are ready.
            if (onDone) onDone();
            break;
          }

          case 'ping':
            // Keep-alive — ignore
            break;

          default:
            break;
        }
      } catch {
        // Invalid JSON — skip
      }
    },
    [projectId, queryClient],
  );

  const retry = useCallback(() => {
    const lastMessage = lastSentMessageRef.current;
    if (!lastMessage || isStreamingRef.current) return;

    // Remove the failed user message and any empty assistant placeholder
    setMessages((prev) => {
      let lastUserIdx = -1;
      for (let i = prev.length - 1; i >= 0; i -= 1) {
        if (prev[i].role === 'user') {
          lastUserIdx = i;
          break;
        }
      }
      if (lastUserIdx === -1) return prev;
      return prev.filter((_, i) => {
        if (i === lastUserIdx) return false;
        if (i === lastUserIdx + 1 && prev[i]?.role === 'assistant' && !prev[i]?.content)
          return false;
        return true;
      });
    });

    setError(null);
    sendMessage(lastMessage);
  }, [sendMessage]);

  return {
    messages,
    artifacts,
    isStreaming,
    error,
    sendMessage,
    stopStreaming,
    retry,
    setMessages,
    setArtifacts,
    activeToolSteps,
  };
}
